/**
 * Shared test data interpretation module
 *
 * Contains interpretTestData function, TEST_EXPLANATIONS, and ENERGY_TEST_EXPLANATIONS
 * Used by: StockScreener, PortfolioTableEnhanced, MacroEnergy
 */

// Pulls the DCF fair-value verdict out of a convex_tests object so the authed
// Screener/Portfolio can show the same headline the public grade card does
// ("DCF fair value $X · N% above/upside to fair value"). Returns null when the
// dcf_upside test isn't computable or lacks the raw numbers.
// The one fair-value read (mirrors backend public_grade.dcf_verdict). Upside =
// (FV - price) / price; "above fair value" = (price - FV) / FV. It used to
// divide the "above" case by price (TSLA "96% above" for ~2,245% above). A
// fair value under 1/4 or over 4x the price means the model broke down for the
// company, so nothing is shown.
export function dcfVerdict(fairValue, price) {
  const fv = Number(fairValue);
  const p = Number(price);
  if (!isFinite(fv) || !isFinite(p) || fv <= 0 || p <= 0) return null;
  const r = fv / p;
  if (r < 0.25 || r > 4) return null;
  const upside = (fv - p) / p * 100;
  const above = (p - fv) / fv * 100;
  const verdict = upside >= 5 ? `${Math.round(upside)}% upside to fair value`
    : above >= 5 ? `${Math.round(above).toLocaleString()}% above fair value`
    : 'fairly valued';
  const color = upside >= 5 ? '#22c55e' : above >= 5 ? '#f97316' : '#94A3B8';
  return { fairValue: fv, upsidePct: upside, abovePct: above, verdict, color };
}

export function extractDcfVerdict(convexTests, livePrice) {
  const t = convexTests && convexTests.dcf_upside;
  if (!t || t.computable === false) return null;
  const raw = t.raw || {};
  const fairValue = raw.dcf_value ?? raw.dcf ?? raw.intrinsicValue;
  const price = livePrice ?? raw.current_price ?? raw.price ?? raw.stockPrice;
  return dcfVerdict(fairValue, price);
}

// Centralized display-name helper. Prefers the curated `title` from
// TEST_EXPLANATIONS / ENERGY_TEST_EXPLANATIONS over a raw snake_case format.
// Keeps the test-row labels consistent with the popup titles users see.
export const displayTestName = (name) => {
  if (!name) return '';
  // Note: TEST_EXPLANATIONS is defined later in the file; the lookup is
  // resolved at call time, not at module-evaluation time, so the forward
  // reference is fine.
  const exp = (typeof TEST_EXPLANATIONS !== 'undefined' && TEST_EXPLANATIONS[name])
    || (typeof ENERGY_TEST_EXPLANATIONS !== 'undefined' && ENERGY_TEST_EXPLANATIONS[name]);
  if (exp && exp.title) return exp.title;
  return name.replace(/_/g, ' ');
};

// Data-coverage summary for a pillar's tests. A pillar score can read ~50
// either because the metrics genuinely landed mid-range OR because too few
// tests had data and the backend fell back to a neutral 50. This lets the UI
// distinguish "measured" from "thin data" so a 50 isn't silently overloaded.
// A test counts as measured when computable !== false and not_applicable !== true.
// Returns { measured, total, ratio, low } or null when there are no tests.
// `low` mirrors the backend's <40% coverage floor (below which the aggregate
// is forced to a neutral 50).
export const pillarCoverage = (tests) => {
  if (!tests || typeof tests !== 'object') return null;
  const entries = Object.values(tests).filter((t) => t && typeof t === 'object');
  const total = entries.length;
  if (!total) return null;
  const measured = entries.filter(
    (t) => t.computable !== false && t.not_applicable !== true
  ).length;
  const ratio = measured / total;
  return { measured, total, ratio, low: ratio < 0.4 };
};

// Function to interpret raw test data into human-readable sentences
export const interpretTestData = (testName, testData, symbol = '') => {
  if (!testData) return null;

  const raw = testData.raw || {};
  const score = testData.score_0to1 ?? testData.value ?? 0.5;
  const scorePercent = (score * 100).toFixed(0);

  // Quality Tests Interpretations
  if (testName === 'growth_orders') {
    const revGrowth = raw.revenue_growth_yoy ?? raw.revenue_growth;
    const epsGrowth = raw.eps_growth_yoy ?? raw.eps_growth;
    if (revGrowth !== undefined) {
      const revStr = (revGrowth * 100).toFixed(1);
      if (score >= 0.7) {
        return `Strong growth: ${symbol}'s revenue grew ${revStr}% year-over-year, indicating healthy business momentum. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Moderate growth: ${symbol}'s revenue grew ${revStr}% YoY, which is acceptable but not exceptional. Score: ${scorePercent}%`;
      } else {
        return `Weak growth: ${symbol}'s revenue ${revGrowth >= 0 ? 'grew only' : 'declined'} ${revStr}% YoY, signaling potential headwinds. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Growth score: ${scorePercent}%`;
  }

  if (testName === 'execution_delivery') {
    const opMargin = raw.operating_margin ?? raw.operatingMargin;
    const grossMargin = raw.gross_margin ?? raw.grossMargin;
    const marginTrend = raw.margin_trend;
    if (opMargin !== undefined) {
      const marginStr = (opMargin * 100).toFixed(1);
      if (score >= 0.7) {
        return `Strong execution: ${symbol} maintains ${marginStr}% operating margin${marginTrend === 'expanding' ? ' with expanding margins' : ''}. Management is delivering efficiently. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Adequate execution: ${symbol}'s ${marginStr}% operating margin is within acceptable range. Score: ${scorePercent}%`;
      } else {
        return `Execution concerns: ${symbol}'s ${marginStr}% operating margin is below peers, suggesting operational challenges. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Execution score: ${scorePercent}%`;
  }

  if (testName === 'roic_vs_wacc') {
    const roic = raw.roic ?? raw.returnOnInvestedCapital;
    const wacc = raw.wacc ?? 0.08;
    if (roic !== undefined) {
      const roicStr = (roic * 100).toFixed(1);
      const spread = ((roic - wacc) * 100).toFixed(1);
      if (score >= 0.7) {
        return `Value creation: ${symbol}'s ROIC of ${roicStr}% exceeds cost of capital by ${spread}pp, creating shareholder value. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Marginal returns: ${symbol}'s ROIC of ${roicStr}% is close to its cost of capital. Limited value creation. Score: ${scorePercent}%`;
      } else {
        return `Value destruction: ${symbol}'s ROIC of ${roicStr}% is below cost of capital, destroying shareholder value. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `ROIC score: ${scorePercent}%`;
  }

  if (testName === 'moat_strength') {
    const moatType = raw.moat_classification || raw.moat_type;
    const grossMargin = raw.gross_margin ?? raw.grossMargin;
    if (moatType) {
      return `Competitive position: ${symbol} classified as "${moatType}" moat${grossMargin ? ` with ${(grossMargin * 100).toFixed(0)}% gross margin` : ''}. Score: ${scorePercent}%`;
    }
    if (score >= 0.7) {
      return `Strong moat: ${symbol} shows durable competitive advantages through high margins and returns. Score: ${scorePercent}%`;
    }
    return testData.notes || `Moat score: ${scorePercent}%`;
  }

  if (testName === 'fcf_yield') {
    const fcfYield = raw.fcf_yield ?? raw.freeCashFlowYield;
    if (fcfYield !== undefined) {
      const yieldStr = (fcfYield * 100).toFixed(1);
      if (score >= 0.7) {
        return `Attractive cash generation: ${symbol} yields ${yieldStr}% FCF, providing good shareholder returns potential. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Moderate FCF: ${symbol}'s ${yieldStr}% FCF yield is acceptable given growth profile. Score: ${scorePercent}%`;
      } else {
        return `Low cash yield: ${symbol}'s ${yieldStr}% FCF yield suggests limited near-term cash returns. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `FCF Yield score: ${scorePercent}%`;
  }

  if (testName === 'debt_health') {
    const debtToEquity = raw.debt_to_equity ?? raw.debtToEquity;
    const interestCoverage = raw.interest_coverage ?? raw.interestCoverage;
    if (debtToEquity !== undefined) {
      if (score >= 0.7) {
        return `Healthy balance sheet: ${symbol} has D/E of ${debtToEquity.toFixed(2)}${interestCoverage ? ` and ${interestCoverage.toFixed(1)}x interest coverage` : ''}. Low financial risk. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Moderate leverage: ${symbol}'s D/E of ${debtToEquity.toFixed(2)} is manageable but worth monitoring. Score: ${scorePercent}%`;
      } else {
        return `High leverage concern: ${symbol}'s D/E of ${debtToEquity.toFixed(2)} indicates elevated financial risk. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Debt health score: ${scorePercent}%`;
  }

  if (testName === 'financial_health') {
    const altmanZ = raw.altman_z ?? raw.altmanZScore ?? raw.altman_z_score;
    const piotroski = raw.piotroski ?? raw.piotroskiScore ?? raw.piotroski_score;
    let interpretation = '';
    if (altmanZ !== undefined) {
      if (altmanZ > 3) interpretation += `${symbol}'s Altman Z-Score of ${parseFloat(altmanZ).toFixed(1)} indicates very low bankruptcy risk, placing it firmly in the "safe zone." `;
      else if (altmanZ > 1.8) interpretation += `${symbol}'s Altman Z-Score of ${parseFloat(altmanZ).toFixed(1)} is in the "grey zone," meaning financial health should be monitored closely. `;
      else interpretation += `${symbol}'s Altman Z-Score of ${parseFloat(altmanZ).toFixed(1)} indicates elevated distress risk, warranting careful scrutiny of the balance sheet. `;
    }
    if (piotroski !== undefined) {
      const p = parseInt(piotroski);
      if (p >= 7) interpretation += `The Piotroski F-Score of ${p}/9 confirms strong fundamental quality across profitability, leverage, and operating efficiency metrics.`;
      else if (p >= 4) interpretation += `The Piotroski F-Score of ${p}/9 shows average fundamental quality — some metrics are strong while others need improvement.`;
      else interpretation += `The Piotroski F-Score of ${p}/9 flags weak fundamentals across profitability, leverage, or operating efficiency, suggesting potential value traps.`;
    }
    if (interpretation) return `${interpretation} Score: ${scorePercent}%`;
    if (testData.notes) return `${symbol}: ${testData.notes} Score: ${scorePercent}%`;
    return `Financial health score: ${scorePercent}%`;
  }

  if (testName === 'analyst_price_target') {
    const currentPrice = raw.current_price ?? raw.price;
    const targetPrice = raw.target_price ?? raw.targetConsensus ?? raw.targetMedian;
    if (currentPrice && targetPrice) {
      const upside = ((targetPrice / currentPrice) - 1) * 100;
      if (upside > 20) {
        return `Bullish analyst view: Consensus target of $${targetPrice.toFixed(0)} implies ${upside.toFixed(0)}% upside from current $${currentPrice.toFixed(2)}. Score: ${scorePercent}%`;
      } else if (upside > 0) {
        return `Neutral analyst view: Target of $${targetPrice.toFixed(0)} suggests ${upside.toFixed(0)}% upside from $${currentPrice.toFixed(2)}. Score: ${scorePercent}%`;
      } else {
        return `Bearish analyst view: Target of $${targetPrice.toFixed(0)} implies ${Math.abs(upside).toFixed(0)}% downside from $${currentPrice.toFixed(2)}. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Price target score: ${scorePercent}%`;
  }

  // Setup Tests Interpretations
  if (testName === 'trend_health') {
    const aboveSMA50 = raw.above_sma50 ?? raw.aboveSMA50 ?? raw.price_above_50dma;
    const aboveSMA200 = raw.above_sma200 ?? raw.aboveSMA200 ?? raw.price_above_200dma;
    const goldenCross = raw.ma50_above_200;
    const smasAbove = [aboveSMA50, aboveSMA200].filter(x => x === true).length;
    const crossType = goldenCross === true ? 'golden cross (bullish)' : goldenCross === false ? 'death cross (bearish)' : '';
    if (score >= 0.7) {
      return `Strong uptrend: ${symbol} is trading above its 50-day and 200-day moving averages${crossType ? ` with a ${crossType}` : ''}. All major trend indicators confirm bullish momentum, making this a favorable technical setup for continued upside. Score: ${scorePercent}%`;
    } else if (score >= 0.4) {
      return `Mixed trend: ${symbol} is above ${smasAbove}/2 key moving averages${crossType ? ` (${crossType})` : ''}. The trend is unclear with conflicting signals between short-term and long-term momentum. Consider waiting for clearer direction. Score: ${scorePercent}%`;
    } else {
      return `Downtrend: ${symbol} is trading below its major moving averages${crossType ? ` with a ${crossType}` : ''}, indicating sustained bearish price action. Technical conditions suggest caution and waiting for trend reversal before adding exposure. Score: ${scorePercent}%`;
    }
  }

  if (testName === 'relative_strength') {
    const relStrength = raw.relative_strength ?? raw.relativeStrength ?? raw.vs_spy ?? raw.rel_strength;
    if (relStrength !== undefined) {
      const relStr = (relStrength * 100).toFixed(1);
      if (score >= 0.7) {
        return `Outperforming market: ${symbol} has outperformed SPY by ${relStr}% over the measurement period, showing strong institutional demand and relative momentum. Stocks that consistently outperform the broader market tend to attract further buying. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Market performer: ${symbol} is roughly in line with SPY at ${relStr}% relative performance. The stock is tracking the broader market without significant outperformance or underperformance. Score: ${scorePercent}%`;
      } else {
        return `Lagging market: ${symbol} has underperformed SPY by ${Math.abs(parseFloat(relStr))}%, signaling weak demand relative to the broader market. Persistent underperformance may indicate fading institutional interest or sector rotation away from this name. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Relative strength score: ${scorePercent}%`;
  }

  if (testName === 'sector_momentum') {
    const sector = raw.sector || 'Unknown';
    const etf = raw.etf || '';
    const sectorRel = raw.sector_rel;
    const overrideApplied = raw.override_applied === true;
    const peerGroup = overrideApplied
      ? `${symbol}'s peer-group ETF ${etf} (override applied — GICS sector "${sector}" doesn't reflect how the market treats this name)`
      : `${sector} sector (${etf})`;
    if (sectorRel !== undefined) {
      const relPct = (sectorRel * 100).toFixed(1);
      if (score >= 0.7) {
        return `${peerGroup} is outperforming SPY by ${relPct}% over 20 days. ${symbol} benefits from sector tailwinds. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `${peerGroup} is performing roughly in line with SPY (${relPct}% over 20 days). No significant sector tailwind or headwind for ${symbol}. Score: ${scorePercent}%`;
      } else {
        return `${peerGroup} is lagging SPY by ${Math.abs(parseFloat(relPct))}% over 20 days. ${symbol} faces sector headwinds regardless of its individual merit. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Sector momentum score: ${scorePercent}%`;
  }

  if (testName === 'rsi_momentum') {
    const rsi = raw.rsi ?? raw.rsi_14;
    if (rsi !== undefined) {
      if (rsi < 30) {
        return `Oversold: ${symbol}'s RSI of ${rsi.toFixed(0)} indicates oversold conditions - potential bounce ahead. Score: ${scorePercent}%`;
      } else if (rsi < 50) {
        return `Recovering: ${symbol}'s RSI of ${rsi.toFixed(0)} shows momentum building from oversold levels. Score: ${scorePercent}%`;
      } else if (rsi < 70) {
        return `Healthy momentum: ${symbol}'s RSI of ${rsi.toFixed(0)} shows balanced momentum, neither overbought nor oversold. Score: ${scorePercent}%`;
      } else {
        return `Overbought: ${symbol}'s RSI of ${rsi.toFixed(0)} indicates overbought conditions - caution on new entries. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `RSI score: ${scorePercent}%`;
  }

  if (testName === 'price_momentum_5_21') {
    const mom5d = raw.momentum_5d_pct ?? raw.momentum_5d;
    const mom21d = raw.momentum_21d_pct ?? raw.momentum_21d;
    if (mom5d !== undefined && mom21d !== undefined) {
      if (score >= 0.7) {
        return `Strong momentum: ${symbol} is up ${mom5d.toFixed(1)}% (5d) and ${mom21d.toFixed(1)}% (21d), showing sustained buying pressure. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Mixed momentum: ${symbol} shows ${mom5d >= 0 ? '+' : ''}${mom5d.toFixed(1)}% (5d), ${mom21d >= 0 ? '+' : ''}${mom21d.toFixed(1)}% (21d). Score: ${scorePercent}%`;
      } else {
        return `Weak momentum: ${symbol} is down ${Math.abs(mom5d).toFixed(1)}% (5d) and ${Math.abs(mom21d).toFixed(1)}% (21d), showing selling pressure. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Price momentum score: ${scorePercent}%`;
  }

  if (testName === 'post_earnings_reaction') {
    const drift = raw.post_earnings_drift ?? raw.earnings_drift ?? raw.drift_pct;
    if (drift !== undefined) {
      const driftStr = (drift * 100).toFixed(1);
      if (drift > 0.03) {
        return `Positive earnings reception: ${symbol} drifted +${driftStr}% after earnings, market digesting good results. Score: ${scorePercent}%`;
      } else if (drift > -0.03) {
        return `Neutral earnings reaction: ${symbol} moved ${drift >= 0 ? '+' : ''}${driftStr}% post-earnings. Score: ${scorePercent}%`;
      } else {
        return `Negative earnings reaction: ${symbol} declined ${Math.abs(parseFloat(driftStr))}% after earnings, suggesting disappointment. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Post-earnings score: ${scorePercent}%`;
  }

  if (testName === 'accumulation_volume') {
    const obvSlope = raw.obv_slope ?? raw.obvSlope ?? raw.slope;
    if (obvSlope !== undefined || raw.norm !== undefined) {
      if (score >= 0.7) {
        return `Accumulation detected: ${symbol}'s On-Balance Volume (OBV) is rising, indicating that institutional buyers are systematically accumulating shares. Volume is confirming the uptrend, which is a bullish signal for continued price appreciation. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Neutral volume flow: ${symbol}'s OBV is relatively flat, showing no clear pattern of institutional accumulation or distribution. Volume activity is inconclusive and does not strongly favor buyers or sellers. Score: ${scorePercent}%`;
      } else {
        return `Distribution detected: ${symbol}'s OBV is declining, suggesting that institutional holders are systematically selling shares. Volume is confirming downward pressure, which is a bearish signal that could precede further price weakness. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Accumulation score: ${scorePercent}%`;
  }

  if (testName === 'valuation_vs_history') {
    const pe = raw.pe_ratio ?? raw.peRatio ?? raw.pe;
    const peg = raw.peg_ratio ?? raw.pegRatio;
    if (pe !== undefined) {
      if (score >= 0.7) {
        return `Attractive valuation: ${symbol} trades at ${pe.toFixed(1)}x P/E${peg ? `, PEG of ${peg.toFixed(2)}` : ''}, below historical average. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Fair valuation: ${symbol}'s ${pe.toFixed(1)}x P/E is roughly in line with its history. Score: ${scorePercent}%`;
      } else {
        return `Expensive valuation: ${symbol}'s ${pe.toFixed(1)}x P/E is elevated relative to history. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Valuation score: ${scorePercent}%`;
  }

  if (testName === 'valuation_blend') {
    const pe = raw.pe_ratio ?? raw.pe;
    const vsHistory = raw.vs_history_score;
    const vsSector = raw.vs_sector_score;
    const peerPe = raw.peer_pe;
    const peerSource = raw.peer_source;
    const ratio = raw.ratio_to_peer;
    const sectorExcepted = raw.sector_excepted;

    const peStr = pe !== undefined && pe !== null ? `${pe.toFixed(1)}x P/E` : 'P/E';
    const lines = [];

    // History sub-signal
    if (vsHistory !== undefined && vsHistory !== null) {
      if (vsHistory >= 0.7) {
        lines.push(`vs own history: ${peStr} sits low in its own 1-year range — historically cheap for ${symbol}`);
      } else if (vsHistory >= 0.4) {
        lines.push(`vs own history: ${peStr} is mid-range vs ${symbol}'s 1-year history`);
      } else {
        lines.push(`vs own history: ${peStr} is near the high end of ${symbol}'s 1-year range — historically expensive`);
      }
    }

    // Sector sub-signal
    if (vsSector !== undefined && vsSector !== null && peerPe) {
      const ratioStr = ratio ? `${ratio.toFixed(2)}x` : '';
      const peerLabel = peerSource ? peerSource.replace(/^(industry|sector):\s*/, '') : 'peer';
      if (vsSector >= 0.7) {
        lines.push(`vs peers: trading at ${ratioStr} of the ${peerLabel} average (${peerPe.toFixed(1)}x) — discounted vs the peer group`);
      } else if (vsSector >= 0.4) {
        lines.push(`vs peers: roughly in-line with the ${peerLabel} average (${peerPe.toFixed(1)}x)`);
      } else {
        lines.push(`vs peers: trading at ${ratioStr} of the ${peerLabel} average (${peerPe.toFixed(1)}x) — premium-priced vs the peer group`);
      }
    } else if (sectorExcepted) {
      lines.push(`vs peers: not applicable for this sector (REITs, biotech, and similar are valued on different metrics than P/E)`);
    } else if (vsHistory !== null) {
      lines.push(`vs peers: peer-group P/E data unavailable — score reflects history only`);
    }

    if (lines.length > 0) {
      return `${lines.join('. ')}. Combined score: ${scorePercent}%`;
    }
    return testData.notes || `Valuation score: ${scorePercent}%`;
  }

  if (testName === 'catalyst_proximity') {
    const daysToEarnings = raw.days_to_earnings ?? raw.daysToEarnings ?? raw.days_until ?? raw.days_to_next_earnings;
    if (daysToEarnings !== undefined) {
      if (daysToEarnings <= 7) {
        return `Imminent catalyst: ${symbol}'s earnings report is just ${daysToEarnings} days away. Expect increased volatility and potential for significant price movement. This is the prime window for positioning ahead of the announcement. Score: ${scorePercent}%`;
      } else if (daysToEarnings <= 30) {
        return `Upcoming catalyst: ${symbol} is scheduled to report earnings in ${daysToEarnings} days. This creates a near-term positioning opportunity as institutional investors begin adjusting their exposure ahead of the event. Score: ${scorePercent}%`;
      } else {
        return `Distant catalyst: ${symbol}'s next earnings report is approximately ${daysToEarnings} days away. With no imminent catalyst, the stock is more likely to trade on broader market sentiment and sector trends rather than company-specific news. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Catalyst score: ${scorePercent}%`;
  }

  if (testName === 'insider_activity') {
    // Composite: 60% level (90d buy share) + 40% velocity (recent 30d vs 30-90d)
    const buyCount = raw.buy_count ?? raw.transaction_count ?? raw.transactionCount;
    const sellCount = raw.sell_count;
    const levelRatio = raw.level_buy_ratio;
    const velocityDelta = raw.velocity_delta;
    const velocityScore = raw.velocity_score;

    if (levelRatio !== undefined && levelRatio !== null) {
      const levelPct = (levelRatio * 100).toFixed(0);
      const countsStr = (buyCount !== undefined && sellCount !== undefined)
        ? ` (${buyCount} buys, ${sellCount} sells over 90d)`
        : '';
      const velStr = (velocityDelta !== null && velocityDelta !== undefined)
        ? ` Velocity: ${velocityDelta >= 0 ? '+' : ''}${(velocityDelta * 100).toFixed(0)}pp shift in buy share between the last 30d and the prior 60d.`
        : ' Velocity not available (one window had no transactions, falling back to level only).';
      if (score >= 0.7) {
        return `Insider conviction: ${symbol} insiders are net buyers at ${levelPct}% buy share${countsStr}.${velStr} Composite score: ${scorePercent}%`;
      } else if (score >= 0.45) {
        return `Mixed insider activity: ${symbol} insider buy share is ${levelPct}%${countsStr}.${velStr} Composite score: ${scorePercent}%`;
      } else {
        return `Insider distribution: ${symbol} insiders are net sellers at ${levelPct}% buy share${countsStr}.${velStr} Composite score: ${scorePercent}%`;
      }
    }
    if (testData.proxy) {
      return `Insider data unavailable for ${symbol} - using financial health proxy (Piotroski + analyst consensus). Score: ${scorePercent}%`;
    }
    return testData.notes || `Insider activity score: ${scorePercent}%`;
  }

  if (testName === 'estimate_dispersion_velocity') {
    const velPct = raw.velocity_pct;
    const recentDisp = raw.recent_dispersion;
    const priorDisp = raw.prior_dispersion;
    const recentN = raw.recent_target_count;
    const priorN = raw.prior_target_count;
    if (velPct !== undefined && velPct !== null) {
      const vPct = (velPct * 100).toFixed(0);
      const rd = (recentDisp * 100).toFixed(1);
      const pd = (priorDisp * 100).toFixed(1);
      const direction = velPct <= -0.10 ? 'tightening (consensus building)' : velPct >= 0.10 ? 'widening (uncertainty rising)' : 'stable';
      return `Analyst price-target dispersion is ${direction} for ${symbol}: ${rd}% spread over the last 60 days vs ${pd}% in the prior 60-day window (${velPct >= 0 ? '+' : ''}${vPct}% change). Based on ${recentN} recent and ${priorN} prior analyst targets. Score: ${scorePercent}%`;
    }
    return testData.notes || `Dispersion velocity not computable for ${symbol} — analyst coverage is too thin in one of the two 60-day windows (need 3+ price targets in each).`;
  }

  if (testName === 'short_volume_velocity') {
    const velPct = raw.velocity_pct;
    const recentMean = raw.recent_short_pct_mean;
    const priorMean = raw.prior_short_pct_mean;
    const earliestDate = raw.earliest_trading_date;
    const latestDate = raw.latest_trading_date;
    if (velPct !== undefined && velPct !== null && recentMean !== undefined) {
      const vPct = (velPct * 100).toFixed(1);
      const rPct = (recentMean * 100).toFixed(1);
      const pPct = (priorMean * 100).toFixed(1);
      let direction;
      if (velPct > 0.25) direction = 'a sharp ramp in short selling — bearish setup';
      else if (velPct > 0.10) direction = 'short selling building — mildly bearish';
      else if (velPct >= -0.10) direction = 'stable short-selling pressure';
      else if (velPct >= -0.25) direction = 'short selling cooling — mildly bullish';
      else direction = 'a sharp drop in short selling (likely covering) — bullish setup';
      return `${symbol}: ${direction}. In the most recent 20 trading days, ${rPct}% of daily volume came from short selling, vs ${pPct}% in the prior 20-day window (${velPct >= 0 ? '+' : ''}${vPct}% change). Window: ${earliestDate} to ${latestDate}. Score: ${scorePercent}%`;
    }
    const reason = raw.reason;
    if (reason === 'insufficient_history') {
      return `Short selling velocity for ${symbol} isn't computable yet: only ${raw.days_available} trading days of short-volume data ingested (need 36+). Run the FINRA backfill from the admin endpoint to populate.`;
    }
    return testData.notes || `Short selling velocity not computable for ${symbol}.`;
  }

  if (testName === 'institutional_flow_delta') {
    const deltaPct = raw.share_delta_pct;
    const latestShares = raw.latest_total_shares;
    const priorShares = raw.prior_total_shares;
    const latestHolders = raw.latest_holder_count;
    const priorHolders = raw.prior_holder_count;
    const latestQuarter = raw.latest_quarter_end;
    const priorQuarter = raw.prior_quarter_end;
    const staleSkipped = raw.stale_quarter_skipped;
    if (deltaPct !== undefined && deltaPct !== null && latestShares !== undefined) {
      const dPct = (deltaPct * 100).toFixed(2);
      const holderDelta = (latestHolders ?? 0) - (priorHolders ?? 0);
      const holderChange = holderDelta >= 0
        ? `${holderDelta} more institutional holders`
        : `${Math.abs(holderDelta)} fewer institutional holders`;
      let direction;
      if (deltaPct > 0.07) direction = 'heavy accumulation by institutions';
      else if (deltaPct > 0.03) direction = 'accumulation by institutions';
      else if (deltaPct > 0.01) direction = 'light accumulation';
      else if (deltaPct >= -0.01) direction = 'flat ownership';
      else if (deltaPct >= -0.03) direction = 'light distribution';
      else if (deltaPct >= -0.07) direction = 'distribution';
      else direction = 'heavy distribution';
      const stalenote = staleSkipped
        ? ` (skipped ${staleSkipped} as it was still within the 45-day 13F filing window and had incomplete holder coverage)`
        : '';
      return `${symbol}: ${direction}. ${latestShares.toLocaleString()} institutional shares as of ${latestQuarter} (${latestHolders} holders) vs ${priorShares.toLocaleString()} in the prior quarter ${priorQuarter} (${priorHolders} holders) — ${deltaPct >= 0 ? '+' : ''}${dPct}% share change, ${holderChange}${stalenote}. Score: ${scorePercent}%`;
    }
    // Non-computable path — read reason field
    const reason = raw.reason;
    if (reason === 'insufficient_history') {
      return `Institutional flow delta for ${symbol} isn't computable yet: only ${raw.quarters_available} quarter(s) of 13F data on file (need 2+). Run the SEC EDGAR backfill from the admin endpoint to populate.`;
    }
    return testData.notes || `Institutional flow delta not computable for ${symbol}.`;
  }

  if (testName === 'capex_trend_acceleration') {
    const accelPct = raw.capex_accel_pct;
    const revGrowth = raw.revenue_growth_pct;
    const recentRatio = raw.recent_capex_revenue_ratio;
    const priorRatio = raw.prior_capex_revenue_ratio;
    const capexDir = raw.capex_direction;
    const revDir = raw.revenue_direction;
    if (accelPct !== undefined && accelPct !== null) {
      const aPct = (accelPct * 100).toFixed(0);
      const rPct = (revGrowth * 100).toFixed(0);
      const rr = (recentRatio * 100).toFixed(1);
      const pr = (priorRatio * 100).toFixed(1);
      let summary;
      if (capexDir === 'up' && revDir === 'up') {
        summary = `productive investment — capex growing alongside revenue growth, which historically signals durable demand`;
      } else if (capexDir === 'up' && revDir === 'down') {
        summary = `over-investment risk — capex growing while revenue declines, the classic red flag in Titman/Wei/Xie (2004)`;
      } else if (capexDir === 'up' && revDir === 'flat') {
        summary = `capital intensity rising without revenue lift — the bet is unproven`;
      } else if (capexDir === 'down' && revDir === 'up') {
        summary = `capex discipline with growing revenue — pointing toward margin expansion`;
      } else if (capexDir === 'down' && revDir === 'down') {
        summary = `defensive capex cut alongside declining revenue — appropriate but not bullish`;
      } else {
        summary = `capex steady at current revenue scale`;
      }
      return `${symbol}: ${summary}. Capex/revenue ratio went from ${pr}% (prior 2Q avg) to ${rr}% (recent 2Q avg) — a ${accelPct >= 0 ? '+' : ''}${aPct}% change — while revenue ${revGrowth >= 0 ? 'grew' : 'declined'} ${Math.abs(revGrowth * 100).toFixed(0)}% over the same windows. Score: ${scorePercent}%`;
    }
    return testData.notes || `Capex trend not computable for ${symbol} — need 4+ matched quarters of cash flow and revenue data.`;
  }

  if (testName === 'analyst_sentiment') {
    const upside = raw.upside_pct ?? raw.upsidePct ?? raw.upside;
    const targetPrice = raw.target_consensus ?? raw.target_price ?? raw.targetConsensus;
    const currentPrice = raw.current_price ?? raw.price;
    if (upside !== undefined) {
      const upsideStr = (upside * 100).toFixed(0);
      const priceInfo = (targetPrice && currentPrice) ? ` Consensus price target is $${parseFloat(targetPrice).toFixed(0)} vs current price of $${parseFloat(currentPrice).toFixed(2)}.` : '';
      if (upside > 0.2) {
        return `Bullish analysts: Wall Street consensus sees ${upsideStr}% upside for ${symbol}.${priceInfo} Strong analyst conviction suggests the market may be underpricing this stock's potential. Score: ${scorePercent}%`;
      } else if (upside > 0) {
        return `Neutral analysts: Consensus implies ${upsideStr}% upside for ${symbol}.${priceInfo} Analysts see modest room for appreciation but no strong conviction in either direction. Score: ${scorePercent}%`;
      } else {
        return `Bearish analysts: Consensus implies ${Math.abs(parseInt(upsideStr))}% downside for ${symbol}.${priceInfo} The stock is trading above analyst targets, suggesting potential overvaluation risk. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Analyst sentiment score: ${scorePercent}%`;
  }

  // Setup Tests without explicit handlers
  if (testName === 'positioning_sentiment') {
    const drawdown = raw.drawdown_from_52w_high ?? raw.drawdown;
    if (drawdown !== undefined) {
      const ddPct = (drawdown * 100).toFixed(1);
      if (score >= 0.7) {
        return `Strong positioning: ${symbol} is only ${ddPct}% below its 52-week high, indicating strong institutional support and bullish sentiment. Buyers are defending price levels near the highs. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Moderate positioning: ${symbol} is ${ddPct}% below its 52-week high, a moderate pullback that could present an opportunity if fundamentals remain intact. Score: ${scorePercent}%`;
      } else {
        return `Weak positioning: ${symbol} has pulled back ${ddPct}% from its 52-week high, suggesting significant selling pressure and weakening sentiment among institutional holders. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Positioning sentiment score: ${scorePercent}%`;
  }

  if (testName === 'estimate_revisions') {
    const r21 = raw.r21 ?? raw.momentum_1m;
    const r63 = raw.r63 ?? raw.momentum_3m;
    const revScore = raw.revision_score_0to1;
    if (revScore !== undefined) {
      if (score >= 0.7) {
        return `Positive revisions: Analysts are raising their estimates for ${symbol}, indicating improving business outlook and potential for earnings beats. This is a strong bullish signal. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Mixed revisions: Analyst estimates for ${symbol} are relatively stable with no clear trend in revisions. The consensus outlook is holding steady. Score: ${scorePercent}%`;
      } else {
        return `Negative revisions: Analysts are cutting estimates for ${symbol}, suggesting deteriorating fundamentals or lowered expectations for upcoming earnings. Score: ${scorePercent}%`;
      }
    }
    if (r21 !== undefined && r63 !== undefined) {
      const r21Str = (r21 * 100).toFixed(1);
      const r63Str = (r63 * 100).toFixed(1);
      const delta = ((r21 - r63) * 100).toFixed(1);
      if (score >= 0.7) {
        return `Improving momentum: ${symbol}'s 1-month return (${r21Str}%) exceeds its 3-month return (${r63Str}%), with a +${delta}% delta suggesting accelerating price action and likely positive estimate revisions. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Stable momentum: ${symbol}'s 1-month (${r21Str}%) and 3-month (${r63Str}%) returns are similar (delta ${delta}%), suggesting stable expectations. No strong revision signal detected. Score: ${scorePercent}%`;
      } else {
        return `Decelerating momentum: ${symbol}'s 1-month return (${r21Str}%) lags its 3-month return (${r63Str}%), with a ${delta}% delta suggesting fading momentum and potentially negative estimate revisions. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `Estimate revisions score: ${scorePercent}%`;
  }

  // Convex Tests Interpretations
  if (testName === 'orders_demand') {
    const revGrowthYoY = raw.revenue_growth_yoy;
    const accel = raw.accel;
    if (revGrowthYoY !== undefined) {
      const revStr = (revGrowthYoY * 100).toFixed(1);
      if (score >= 0.7) {
        return `Strong demand signal: ${symbol}'s revenue is growing ${revStr}% year-over-year${accel !== undefined && accel > 0 ? `, accelerating by +${(accel * 100).toFixed(1)}pp` : ''}. This level of topline growth signals robust customer demand and potential for asymmetric upside. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Moderate demand: ${symbol}'s revenue is growing ${revStr}% year-over-year${accel !== undefined ? ` (${accel >= 0 ? 'stable' : 'decelerating'})` : ''}. Demand indicators are neutral without a strong directional signal. Score: ${scorePercent}%`;
      } else {
        return `Weak demand: ${symbol}'s revenue growth of ${revStr}% is below average${accel !== undefined && accel < 0 ? `, and decelerating by ${(accel * 100).toFixed(1)}pp` : ''}. Fading topline growth limits convex opportunity. Score: ${scorePercent}%`;
      }
    }
    if (score >= 0.7) {
      return `Strong demand signal: ${symbol} showing positive breakout with volume expansion, suggesting institutional accumulation and potential for asymmetric upside. Score: ${scorePercent}%`;
    } else if (score >= 0.4) {
      return `Moderate demand: ${symbol}'s order/demand indicators are neutral with no strong directional signal from price action or volume. Score: ${scorePercent}%`;
    } else {
      return `Weak demand: ${symbol} showing decelerating price action with below-average volume. Limited convex opportunity at current levels. Score: ${scorePercent}%`;
    }
  }

  if (testName === 'capacity_constraints') {
    const grossMargin = raw.gross_margin;
    const grossMarginPrev = raw.gross_margin_prev;
    const opMargin = raw.operating_margin;
    const opMarginPrev = raw.operating_margin_prev;
    const invGrowth = raw.inventory_growth_yoy;
    if (grossMargin !== undefined && grossMarginPrev !== undefined) {
      const gmPct = (grossMargin * 100).toFixed(1);
      const gmDelta = ((grossMargin - grossMarginPrev) * 100).toFixed(1);
      let marginNote = `${symbol}'s gross margin is ${gmPct}% (${gmDelta > 0 ? '+' : ''}${gmDelta}pp YoY)`;
      if (invGrowth !== undefined) marginNote += `, inventory ${(invGrowth * 100) > 0 ? 'up' : 'down'} ${Math.abs(invGrowth * 100).toFixed(0)}%`;
      if (score >= 0.7) {
        return `Supply constraints favorable: ${marginNote}. Expanding margins suggest pricing power and supply tightness, creating potential for earnings upside. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Neutral supply/demand: ${marginNote}. Margins are stable with no clear supply constraints or competitive pressure. Score: ${scorePercent}%`;
      } else {
        return `Oversupply risk: ${marginNote}. Contracting margins suggest competitive pressure or rising costs that may compress profitability. Score: ${scorePercent}%`;
      }
    }
    if (opMargin !== undefined && opMarginPrev !== undefined) {
      const opPct = (opMargin * 100).toFixed(1);
      const opDelta = ((opMargin - opMarginPrev) * 100).toFixed(1);
      if (score >= 0.7) {
        return `Supply constraints favorable: ${symbol}'s operating margin is ${opPct}% (${opDelta > 0 ? '+' : ''}${opDelta}pp YoY). Expanding margins suggest pricing power. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Neutral supply/demand: ${symbol}'s operating margin is ${opPct}% (${opDelta > 0 ? '+' : ''}${opDelta}pp YoY). No clear margin pressure. Score: ${scorePercent}%`;
      } else {
        return `Oversupply risk: ${symbol}'s operating margin is ${opPct}% (${opDelta}pp YoY). Contracting margins suggest competitive pressure. Score: ${scorePercent}%`;
      }
    }
    if (score >= 0.7) {
      return `Supply constraints favorable: ${symbol}'s expanding margins suggest pricing power and supply tightness. Score: ${scorePercent}%`;
    } else if (score >= 0.4) {
      return `Neutral supply/demand: ${symbol}'s margins are stable with no clear constraints. Score: ${scorePercent}%`;
    } else {
      return `Oversupply risk: ${symbol}'s contracting margins suggest competitive pressure. Score: ${scorePercent}%`;
    }
  }

  if (testName === 'catalyst_window') {
    const daysToEarnings = raw.days_to_earnings ?? raw.daysToEarnings ?? raw.days_to_next_earnings;
    const daysSinceEarnings = raw.days_since_earnings ?? raw.daysSinceEarnings ?? raw.days_since_last_earnings;
    if (daysToEarnings !== undefined && daysToEarnings <= 14) {
      return `Prime catalyst window: ${symbol} reports earnings in ${daysToEarnings} days, creating an asymmetric opportunity. Options pricing will reflect elevated implied volatility, and a strong beat could trigger an outsized move. This is the optimal window for convex positioning. Score: ${scorePercent}%`;
    }
    if (daysSinceEarnings !== undefined && daysSinceEarnings <= 14) {
      return `Recent catalyst: ${symbol} reported earnings ${daysSinceEarnings} days ago, and the post-earnings drift window is still active. Academic research shows stocks tend to continue drifting in the direction of the earnings surprise for weeks after the announcement. Score: ${scorePercent}%`;
    }
    if (daysToEarnings !== undefined) {
      return `Catalyst window: ${symbol}'s next earnings are ${daysToEarnings} days away${daysSinceEarnings !== undefined ? `, last reported ${daysSinceEarnings} days ago` : ''}. The catalyst window is not yet active for convex positioning. Score: ${scorePercent}%`;
    }
    return testData.notes || `Catalyst window score: ${scorePercent}%`;
  }

  if (testName === 'dcf_upside') {
    const currentPrice = raw.current_price ?? raw.price ?? raw.stockPrice;
    const dcfValue = raw.dcf_value ?? raw.dcf ?? raw.intrinsicValue;
    if (currentPrice && dcfValue) {
      const upside = ((dcfValue / currentPrice) - 1) * 100;
      if (upside > 30) {
        return `Significant undervaluation: ${symbol}'s DCF value of $${dcfValue.toFixed(0)} implies ${upside.toFixed(0)}% upside from $${currentPrice.toFixed(2)}. Score: ${scorePercent}%`;
      } else if (upside > 0) {
        return `Modest undervaluation: ${symbol}'s DCF suggests ${upside.toFixed(0)}% upside to fair value. Score: ${scorePercent}%`;
      } else {
        return `Overvaluation: ${symbol} trades ${Math.abs(upside).toFixed(0)}% above DCF value of $${dcfValue.toFixed(0)}. Score: ${scorePercent}%`;
      }
    }
    return testData.notes || `DCF upside score: ${scorePercent}%`;
  }

  // Energy/Macro Test Interpretations
  if (testName === 'system_liquidity') {
    const avgDv = raw.avg_dollar_vol_20d ?? raw.avgDollarVolume;
    if (avgDv) {
      const avgDvB = avgDv / 1e9;
      if (score >= 0.7) {
        return `Strong liquidity: SPY 20-day average dollar volume is $${avgDvB.toFixed(1)}B, well above normal. Institutions are actively participating. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Moderate liquidity: SPY 20-day average dollar volume is $${avgDvB.toFixed(1)}B, within normal range. Score: ${scorePercent}%`;
      } else {
        return `Weak liquidity: SPY 20-day average dollar volume is $${avgDvB.toFixed(1)}B, below average. Reduced institutional activity. Score: ${scorePercent}%`;
      }
    }
    if (raw.fallback) return `Using default liquidity value (50%) - SPY data unavailable. Score: ${scorePercent}%`;
    return testData.notes || `System liquidity score: ${scorePercent}%`;
  }

  if (testName === 'credit_spreads') {
    const spreadProxy = raw.spread_proxy;
    const hygReturn = raw.hyg_return_20d;
    const lqdReturn = raw.lqd_return_20d;
    if (spreadProxy !== undefined) {
      const spreadBps = (spreadProxy * 100).toFixed(1);
      if (score >= 0.7) {
        return `Tight spreads (bullish): High-yield bonds outperforming investment-grade, spread proxy at ${spreadBps}%. Credit markets healthy. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Neutral spreads: HYG vs LQD spread proxy at ${spreadBps}%. Credit conditions stable. Score: ${scorePercent}%`;
      } else {
        return `Wide spreads (bearish): HYG underperforming LQD, spread at ${spreadBps}%. Credit stress detected. Score: ${scorePercent}%`;
      }
    }
    if (raw.fallback) return `Using default credit spread value (50%) - bond data unavailable. Score: ${scorePercent}%`;
    return testData.notes || `Credit spreads score: ${scorePercent}%`;
  }

  if (testName === 'market_breadth') {
    const sma200 = raw.sma200;
    const lastClose = raw.last_close;
    if (sma200 && lastClose) {
      const pctFromSma = ((lastClose / sma200) - 1) * 100;
      if (score >= 0.7) {
        return `Bullish breadth: SPY at $${lastClose.toFixed(2)} is ${pctFromSma.toFixed(1)}% above its 200-day SMA ($${sma200.toFixed(2)}). Uptrend confirmed. Score: ${scorePercent}%`;
      } else {
        return `Bearish breadth: SPY at $${lastClose.toFixed(2)} is ${Math.abs(pctFromSma).toFixed(1)}% below its 200-day SMA ($${sma200.toFixed(2)}). Downtrend warning. Score: ${scorePercent}%`;
      }
    }
    if (raw.fallback) return `Using default breadth value (50%) - SPY SMA data unavailable. Score: ${scorePercent}%`;
    return testData.notes || `Market breadth score: ${scorePercent}%`;
  }

  if (testName === 'vol_term_structure') {
    if (raw.proxy) return `Using proxy volatility value (${scorePercent}%) - VIX term structure data unavailable.`;
    if (score >= 0.7) {
      return `Low volatility regime: VIX term structure in contango (normal). Market calm, favorable for risk assets. Score: ${scorePercent}%`;
    } else if (score >= 0.4) {
      return `Moderate volatility: VIX term structure neutral. Normal market conditions. Score: ${scorePercent}%`;
    } else {
      return `High volatility regime: VIX term structure in backwardation. Fear elevated, caution warranted. Score: ${scorePercent}%`;
    }
  }

  if (testName === 'sentiment') {
    if (raw.default) return `Using neutral default sentiment (50%) - sentiment indicators unavailable. Score: ${scorePercent}%`;
    if (score >= 0.7) {
      return `Bullish sentiment: Market participants optimistic. Watch for potential over-exuberance. Score: ${scorePercent}%`;
    } else if (score >= 0.4) {
      return `Neutral sentiment: Market neither overly bullish nor bearish. Score: ${scorePercent}%`;
    } else {
      return `Bearish sentiment: Fear elevated. Contrarian opportunity possible if fundamentals support. Score: ${scorePercent}%`;
    }
  }

  if (testName === 'leadership') {
    const relStrength = raw.rel_strength_63d;
    const nvdaReturn = raw.nvda_return;
    const spyReturn = raw.spy_return;
    if (relStrength !== undefined) {
      const relStr = (relStrength * 100).toFixed(1);
      const nvdaStr = nvdaReturn ? (nvdaReturn * 100).toFixed(1) : 'N/A';
      const spyStr = spyReturn ? (spyReturn * 100).toFixed(1) : 'N/A';
      if (score >= 0.7) {
        return `Strong leadership: NVDA (${nvdaStr}%) outperforming SPY (${spyStr}%) by ${relStr}% over 63 days. Growth/tech leading. Score: ${scorePercent}%`;
      } else if (score >= 0.4) {
        return `Neutral leadership: NVDA (${nvdaStr}%) roughly matching SPY (${spyStr}%). Mixed sector performance. Score: ${scorePercent}%`;
      } else {
        return `Weak leadership: NVDA (${nvdaStr}%) lagging SPY (${spyStr}%) by ${Math.abs(parseFloat(relStr))}%. Defensive rotation possible. Score: ${scorePercent}%`;
      }
    }
    if (raw.fallback) return `Using default leadership value (50%) - NVDA/SPY data unavailable. Score: ${scorePercent}%`;
    return testData.notes || `Market leadership score: ${scorePercent}%`;
  }

  // Default fallback
  if (testData.notes) return `${symbol}: ${testData.notes} Score: ${scorePercent}%`;
  return `${testName.replace(/_/g, ' ')}: Score ${scorePercent}%`;
};

// Test explanations - how each test calculates its score
export const TEST_EXPLANATIONS = {
  // Quality Tests
  growth_orders: {
    title: "Growth Orders",
    description: "Measures revenue growth acceleration by comparing current year-over-year revenue growth to the previous period's growth rate. Higher acceleration indicates improving business momentum.",
    scoring: "Score increases as revenue growth accelerates. Flat or decelerating growth results in lower scores."
  },
  execution_delivery: {
    title: "Execution & Delivery",
    description: "Evaluates management's ability to execute by tracking margin trends (operating or gross margin). Consistently improving margins suggest strong operational control.",
    scoring: "Expanding margins score higher; contracting margins score lower. Stable margins score neutral."
  },
  roic_vs_wacc: {
    title: "ROIC vs WACC",
    description: "Compares Return on Invested Capital to Weighted Average Cost of Capital (8% proxy). Companies generating returns above their cost of capital create value.",
    scoring: "ROIC > 15% scores well; ROIC < WACC scores poorly. The spread determines the final score."
  },
  moat_strength: {
    title: "Moat Strength",
    description: "Assesses competitive advantage using ROIC levels (>35% = wide moat), gross margin levels (>50% = pricing power), and margin stability over time.",
    scoring: "Wide moat (85%+), Narrow moat (70-85%), Some advantage (50-70%), No moat (<50%)."
  },
  fcf_yield: {
    title: "FCF Yield",
    description: "Free Cash Flow divided by Market Cap. Measures how much actual cash the business generates relative to its price.",
    scoring: "FCF yield > 8% scores high; 5-8% is good; <3% scores low. Negative FCF scores very poorly."
  },
  debt_health: {
    title: "Debt Health",
    description: "Combines Debt-to-Equity ratio (lower is better) and Interest Coverage Ratio (higher is better) to assess financial leverage risk.",
    scoring: "D/E < 0.5 and ICR > 5x scores well. D/E > 2 or ICR < 2x indicates financial stress."
  },
  financial_health: {
    title: "Financial Health",
    description: "Uses Altman Z-Score (bankruptcy risk) and Piotroski F-Score (financial strength). Based on FMP Financial Scores endpoint.",
    scoring: "Z-Score > 3 is safe; Piotroski 7-9 is strong. Combined score weighted 60/40."
  },
  analyst_price_target: {
    title: "Analyst Price Target",
    description: "Compares current price to Wall Street consensus price target. Large upside suggests analysts see undervaluation.",
    scoring: ">30% upside scores 1.0; near target (~5%) scores 0.5; >15% downside scores poorly."
  },

  // Setup Tests
  trend_health: {
    title: "Trend Health",
    description: "Analyzes price position relative to key moving averages (SMA20, SMA50, SMA200). Price above all SMAs indicates strong uptrend.",
    scoring: "Above all 3 SMAs = 1.0; Below all 3 = 0.0. Partial alignment scores proportionally."
  },
  relative_strength: {
    title: "Relative Strength",
    description: "Compares stock's recent performance to SPY (S&P 500). Outperforming the market signals institutional interest.",
    scoring: "Outperforming SPY by >10% scores high; underperforming by >10% scores low."
  },
  sector_momentum: {
    title: "Sector Momentum",
    description: "Measures whether the stock's sector is in favor or out of favor by comparing the sector ETF's 20-day return vs SPY. A stock in a surging sector gets a tailwind; a stock in a lagging sector faces headwinds regardless of individual merit. Per-symbol overrides apply for names whose GICS sector doesn't reflect how the market treats them — GOOGL, GOOG, META, AMZN, TSLA, and NFLX are compared against XLK (Technology) instead of their GICS-assigned ETFs.",
    scoring: "Sector outperforming SPY by >5% = high score; underperforming by >5% = low score. Captures sector rotation dynamics that individual relative strength misses."
  },
  positioning_sentiment: {
    title: "Positioning & Sentiment",
    description: "Uses current drawdown from 52-week high as a sentiment proxy. Moderate drawdowns can signal buying opportunities.",
    scoring: "5-15% drawdown (dip buying zone) scores highest; >30% or 0% drawdown scores lower."
  },
  rsi_momentum: {
    title: "RSI Momentum",
    description: "14-period Relative Strength Index identifies overbought (>70) and oversold (<30) conditions.",
    scoring: "RSI 30-50 (oversold recovery) scores best; RSI >85 (extreme overbought) scores worst."
  },
  price_momentum_5_21: {
    title: "Price Momentum 5/21",
    description: "Measures price momentum over 5-day and 21-day windows. Positive short and medium-term momentum indicates buying pressure.",
    scoring: "Strong positive momentum on both timeframes scores high; weak or negative momentum scores low."
  },
  estimate_revisions: {
    title: "Estimate Revisions",
    description: "Tracks analyst estimate changes. Upward revisions often precede price appreciation.",
    scoring: "Positive revisions score high; negative revisions score low. Stability scores neutral."
  },
  post_earnings_reaction: {
    title: "Post-Earnings Reaction",
    description: "Measures price drift in the 5-21 days following earnings. Positive drift suggests market digesting good news.",
    scoring: "Strong positive drift (>5%) scores high; negative drift scores low."
  },
  accumulation_volume: {
    title: "Accumulation Volume",
    description: "On-Balance Volume (OBV) slope over 20 days. Rising OBV indicates accumulation by smart money.",
    scoring: "Positive OBV slope scores high; negative slope (distribution) scores low."
  },
  valuation_vs_history: {
    title: "Valuation vs History",
    description: "Compares current P/E ratio to market average and adjusts for growth (PEG ratio). Lower PEG = better value.",
    scoring: "P/E < 15 with growth scores well; P/E > 40 with low growth scores poorly."
  },
  valuation_blend: {
    title: "Valuation Blend",
    description: "Two-lens valuation test: 50% the stock's P/E vs its own 1-year range (is it cheap relative to its own history?) and 50% the stock's P/E vs its industry/sector average (is it cheap relative to peers?). Captures both Fama-French value factor and Greenblatt's quality-meets-cheap insight. Sectors where P/E is misleading (REITs, biotech) fall back to history-only.",
    scoring: "P/E below 70% of peer average and below 1-year range scores high; P/E at 1.5x peer average or near 1-year highs scores low. Score floor 0.10, ceiling 1.00."
  },
  catalyst_proximity: {
    title: "Catalyst Proximity",
    description: "Days until next earnings announcement. Stocks often move ahead of and after earnings catalysts.",
    scoring: "Within 7 days = 1.0; 21-45 days = 0.3-0.6; >45 days = 0.1."
  },
  insider_activity: {
    title: "Insider Activity",
    description: "Composite of net insider buying level (60%) and velocity (40%). The level is the dollar share of insider buys over the last 90 days. The velocity compares the buy share in the last 30 days vs the prior 30-60 day window — a sharp shift toward buying signals accelerating conviction. Research backing: Lakonishok/Lee (2001) for level, Cohen/Malloy/Pomorski (2012) for velocity.",
    scoring: "Net buying with accelerating velocity scores ~0.85+; mixed activity ~0.45-0.65; net selling with accelerating velocity scores ~0.15. International stocks with no insider data fall back to a financial-health proxy (Piotroski + analyst consensus)."
  },
  analyst_sentiment: {
    title: "Analyst Sentiment",
    description: "Upside/downside to consensus price target from analyst coverage.",
    scoring: ">30% upside = bullish; 0-15% = neutral; >15% downside = bearish."
  },
  estimate_dispersion_velocity: {
    title: "Estimate Dispersion Velocity",
    description: "Tracks the direction of change in analyst price-target dispersion. Each window's dispersion is (max − min) / mean of the individual analyst targets published in that window. We compare the spread over the last 60 days to the spread over the prior 60 days (60-120 days ago). A tightening spread means analysts are converging — consensus is building — which is a forward-looking bullish signal. A widening spread means analysts are diverging — uncertainty is rising. Research backing: Diether/Malloy/Scherbina (2002).",
    scoring: "Velocity ≤ -30% (sharp tightening) → 0.85. -30% to -10% → 0.65-0.85. -10% to +10% (stable) → 0.45-0.55. +10% to +30% (widening) → 0.30-0.45. Velocity > +30% → 0.20. Requires 3+ analyst targets in each 60-day window; otherwise non-computable."
  },
  institutional_flow_delta: {
    title: "Institutional Flow Delta",
    description: "Quarter-over-quarter change in total institutional ownership for the stock. Sourced directly from SEC EDGAR Form 13F-HR filings (no third-party data dependency — same data, but from the upstream source). Total shares held by all reporting institutions are summed per quarter; we compare the latest quarter to the prior quarter. Heavy accumulation by 'smart money' is a bullish forward signal; heavy distribution suggests institutions are exiting. The 45-day filing lag is structural (SEC rules), but Sias (2004) showed this lag doesn't kill the signal. Research backing: Sias (2004), Cohen/Polk/Sias (2003).",
    scoring: "Delta > +7% → 0.85 (heavy accumulation). +3% to +7% → 0.65-0.85. +1% to +3% → 0.55-0.65 (light accumulation). -1% to +1% → 0.45-0.55 (stable). -3% to -1% → 0.35-0.45 (light distribution). -7% to -3% → 0.20-0.35. Delta < -7% → 0.15 (heavy distribution). Requires 2+ quarters of backfilled 13F data; non-computable until the SEC EDGAR backfill has run."
  },
  short_volume_velocity: {
    title: "Short Selling Velocity",
    description: "20-day rolling change in the share of daily trading volume that comes from short selling, sourced directly from FINRA's Reg SHO daily consolidated trading files (the official source — no third-party data dependency). We compare the mean short-volume share of the most recent 20 trading days to the prior 20 days. A rising share means new short positions are being initiated faster than they're being covered — bearish pressure building. A falling share means short positions are being covered or no new shorts are being initiated — bullish setup. Research backing: Boehmer/Jones/Zhang (2020) — direction-of-change in daily short flow carries more forward-return information than the static short-interest level.",
    scoring: "Velocity > +25% → 0.20 (sharp ramp in short selling, bearish). +10% to +25% → 0.30-0.40. -10% to +10% → 0.45-0.55 (stable). -25% to -10% → 0.60-0.75. Velocity < -25% → 0.80 (short covering, bullish). Requires 36+ trading days of ingested data; non-computable until the FINRA backfill has run."
  },

  // Convex Tests
  orders_demand: {
    title: "Orders & Demand",
    description: "Revenue growth acceleration or breakout vs 60-day high with volume expansion. Signals demand surge.",
    scoring: "Accelerating revenue + volume breakout scores high; declining metrics score low."
  },
  capacity_constraints: {
    title: "Capacity Constraints",
    description: "Rising gross margins can indicate supply constraints and pricing power. Good for asymmetric upside.",
    scoring: "Expanding gross margins score high; contracting margins suggest oversupply."
  },
  catalyst_window: {
    title: "Catalyst Window",
    description: "Proximity to earnings (upcoming or recent). Post-earnings drift creates convex opportunities.",
    scoring: "Within 7 days of earnings = peak score; recent earnings (<14 days ago) also scores well."
  },
  dcf_upside: {
    title: "DCF Upside",
    description: "Compares current price to Discounted Cash Flow intrinsic value. Large discounts suggest asymmetric opportunity.",
    scoring: ">50% upside to DCF = 0.85+; fairly valued (~0%) = 0.5; >15% overvalued = <0.35."
  },
  short_interest: {
    title: "Short Interest",
    description: "Short interest as a percentage of float — squeeze potential. High short interest combined with a high days-to-cover figure (short interest / average daily volume) creates conditions where a positive surprise can force a short squeeze, producing asymmetric upside.",
    scoring: "30%+ short interest → 0.85-0.95. 20-30% → 0.70-0.85. 10-20% → 0.55-0.70. 5-10% (normal range) → 0.45-0.55. <5% → 0.30-0.45. Days-to-cover >10 adds +0.10; 5-10 adds +0.05."
  },
  capex_trend_acceleration: {
    title: "Capex Trend Acceleration",
    description: "Productive-investment signal that pairs capex direction with revenue direction. The test compares the average capital-expenditure-to-revenue ratio over the last 2 quarters vs the prior 2 quarters, and then conditions the score on whether revenue grew or shrank over the same windows. Research backing: Titman/Wei/Xie (2004) showed that on average firms with high capex underperform — but the nuance is whether that capex produces revenue. Capex growth paired with revenue growth is the 'investing for growth that's working' signal; capex growth into flat or declining revenue is over-investment that historically drags returns.",
    scoring: "Capex up + revenue up = productive investment (0.70-0.85). Capex up + revenue flat = capital intensity rising (0.45). Capex up + revenue down = over-investment red flag (0.20-0.35). Capex down + revenue up = margin discipline (0.55-0.70). Capex down + revenue down = defensive cut (0.40). Deadbands of ±10% on capex acceleration and ±5% on revenue growth avoid noise-driven label flips. Requires 4+ paired quarters."
  },
  political_trade_flow: {
    title: "Political Trade Flow",
    description: "Net dollar-weighted direction of Senate and House trading disclosures in the ticker over the last 90 days. Politicians' personal trades — especially those clustered before legislation or policy moves — have historically delivered measurable alpha (Ziobrowski et al. 2004, 2011). The effect decays after ~3 months, so the window is intentionally short. Most stocks have no Congressional activity in any given window, in which case the test scores neutral (0.50) rather than absent.",
    scoring: "Net flow > +0.5 (decisive Congressional buying) → 0.70-0.85. +0.2 to +0.5 → 0.58-0.70. -0.2 to +0.2 (balanced) → 0.45-0.58. -0.5 to -0.2 → 0.30-0.45. < -0.5 (decisive selling) → 0.15-0.30. Zero trades in window → 0.50 neutral. Each trade is weighted by the midpoint of its disclosed amount bracket."
  },

  // Crypto Quality tests (backend/scoring/crypto.py, 2026-11-02)
  supply_overhang: {
    title: "Supply Overhang",
    description: "How much of the token's fully diluted value is already circulating. The rest is future supply from unlocks and emissions that dilutes today's holders. Keyrock's 2024 study of ~16,000 unlock events found most put downward pressure on price, starting before the unlock date.",
    scoring: "Market cap ÷ fully diluted value: 95%+ circulating → 1.0, 30% or less → 0.0, linear between."
  },
  liquidity_depth: {
    title: "Liquidity Depth",
    description: "Size and trading depth. Size is one of the core risk factors in crypto (Liu, Tsyvinski & Wu 2022, Journal of Finance), and thin volume means a position can be hard to exit without moving the price.",
    scoring: "70% market cap on a log scale ($100M → 0, $50B+ → 1), 30% daily volume as a share of market cap (0.5% → 0, 5%+ → 1)."
  },
  volatility_maturity: {
    title: "Volatility",
    description: "One-year realized volatility. Low-volatility coins have outperformed high-volatility ones, the crypto version of the low-volatility anomaly (Burggraf & Rudolf 2021, Finance Research Letters).",
    scoring: "Annualized volatility of daily returns: 45% or less → 1.0, 150% or more → 0.0, linear between. Needs 120+ days of prices."
  },
  value_accrual: {
    title: "Value to Token Holders",
    description: "Whether the protocol's revenue actually reaches token holders through buybacks or fee sharing (DefiLlama). A token with no claim on the fees its protocol earns is the crypto version of a weak moat. Not applicable to base chains like Bitcoin.",
    scoring: "Holders paid in the last 30 days → 1.0. Paid in the past year but not the last 30 days → 0.6. Revenue but none to holders → 0.3. No revenue → 0.15."
  },

  // Crypto Convex tests
  usage_momentum: {
    title: "Usage Momentum (fees)",
    description: "Whether paid usage is accelerating: the last 7 days of fees at a 30-day pace vs the last 30 days (DefiLlama). Network adoption predicts crypto returns (Liu & Tsyvinski 2021, Review of Financial Studies; Cong, Karolyi, Tang & Zhao 2022), and fees are paid usage, harder to fake than address counts.",
    scoring: "Weekly pace ÷ 30-day pace: 0.7x or less → 0.0, 1.3x or more → 1.0, linear between."
  },
  valuation_gap: {
    title: "Valuation Gap",
    description: "Market cap ÷ annualized protocol revenue: the crypto analog of the DCF-upside test. Only fee-earning protocols have one; base chains don't.",
    scoring: "8x or less → 1.0, 120x or more → 0.0, log scale between."
  },
  recovery_setup: {
    title: "Recovery Setup",
    description: "Distance below the all-time high, counted as upside only once 30-day strength vs Bitcoin has turned positive (Bitcoin uses its own 30-day return). A deep discount that is still weakening is a falling knife, not convexity.",
    scoring: "Strength turning up: 15% below ATH → 0.4, 75%+ below → 1.0. Still weakening → 0.25."
  }
};

// Macro Energy Test Explanations
export const ENERGY_TEST_EXPLANATIONS = {
  system_liquidity: {
    title: "System Liquidity",
    description: "Measures market liquidity using SPY's 20-day average dollar volume. High liquidity supports risk-on behavior.",
    scoring: "Volume >$60B/day = high liquidity (1.0); <$10B = low liquidity (0.0)."
  },
  credit_spreads: {
    title: "Credit Spreads",
    description: "Tracks HYG (high-yield) vs LQD (investment-grade) bond performance. Tightening spreads = bullish credit.",
    scoring: "HYG outperforming = tight spreads (1.0); HYG lagging = wide spreads (0.0)."
  },
  market_breadth: {
    title: "Market Breadth",
    description: "Whether SPY is above its 200-day moving average. Simple but effective trend filter.",
    scoring: "SPY above 200 SMA = bullish breadth (1.0); below = bearish (0.0)."
  },
  vol_term_structure: {
    title: "Volatility Structure",
    description: "VIX term structure (contango vs backwardation). Contango is normal; backwardation signals fear.",
    scoring: "Normal contango = low vol (0.7+); backwardation = high vol (<0.4)."
  },
  sentiment: {
    title: "Market Sentiment",
    description: "Overall market sentiment indicators. Extreme fear can be contrarian bullish.",
    scoring: "Bullish sentiment = 0.7+; neutral = 0.5; extreme fear = 0.3 (contrarian)."
  },
  leadership: {
    title: "Market Leadership",
    description: "NVDA vs SPY relative strength over 63 days. Tech leadership indicates risk appetite.",
    scoring: "NVDA outperforming by >10% = strong leadership (1.0); lagging = weak (0.0)."
  },
  spy_trend: {
    title: "SPY Trend",
    description: "S&P 500 trend direction based on price vs key moving averages. Bullish market supports risk-on positions.",
    scoring: "SPY above 200 SMA = bullish; below = bearish. Multiple SMA alignment increases score."
  },
  vix_regime: {
    title: "VIX Regime",
    description: "Volatility Index level indicates market fear/complacency. Low VIX = calm markets; high VIX = stress.",
    scoring: "VIX < 15 = calm; 15-25 = normal; 25-35 = elevated; >35 = extreme fear."
  },
  sector_rotation: {
    title: "Sector Rotation",
    description: "Whether money is flowing into risk-on (tech, discretionary) or risk-off (utilities, staples) sectors.",
    scoring: "Risk-on leadership scores high; risk-off leadership scores low for growth stocks."
  },
  breadth: {
    title: "Market Breadth",
    description: "Percentage of stocks above their 50-day moving average. Broad participation = healthy market.",
    scoring: ">70% above 50 SMA = strong breadth; <30% = weak breadth (only leaders working)."
  }
};
